import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Patient } from '../data/usePatients';
import { Prescription, usePrescriptions } from '../data/usePrescriptions';
import { Doctor } from '../types/doctor';
import { PdfReceiptPrescription } from '../types/pdfReceiptPrescription';
import http from '../components/Profile/http';

interface ReceiptState {
  prescriptions: Array<PdfReceiptPrescription> | null;
  signature: string;
  pharmacy: string;
}

const convert = (prescription: Prescription): PdfReceiptPrescription => ({
  id: prescription.id,
  date: prescription.date,
  title: prescription.pharmaceutical_free_text
    ? prescription.pharmaceutical_free_text
    : prescription.title,
  amount: prescription.amount,
  unit: prescription.unit,
  share_of_thc: prescription.share_of_thc,
  share_of_cbd: prescription.share_of_cbd,
  form: prescription.form,
  duration: prescription.duration,
  prescription_text: prescription.prescription_text,
  producer: prescription.producer,
});

export type PdfReceiptForm = {
  patient: Patient;
  doctor: Doctor;
  prescriptions: Array<PdfReceiptPrescription> | null;
  signature: string;
  pharmacy: string;
  onSignatureUpdate: (signature: string) => void;
  onPharmacyUpdate: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isValid: boolean;
  fileName: string;
  formattedIssueDate: string;
  pharmacySelected: boolean;
};

export function usePdfReceipt(
  patient: Patient,
  doctor: Doctor,
  pharmacyId: number | null,
  appointmentId: number,
  forceRefetch: boolean,
): PdfReceiptForm {
  const { results, isLoading, mutate, isRefreshing } = usePrescriptions(patient.id, appointmentId);

  const [receipt, setReceipt] = useState<ReceiptState>({
    prescriptions: null,
    signature: '',
    pharmacy: '',
  });

  useEffect(() => {
    setReceipt((prevState) => ({
      ...prevState,
      prescriptions: null,
    }));
    mutate();
  }, [forceRefetch]);

  useEffect(() => {
    if (!isLoading && !isRefreshing)
      setReceipt((prevState) => ({
        ...prevState,
        prescriptions: results.map(convert),
      }));
  }, [isLoading, isRefreshing]);

  useEffect(() => {
    if (pharmacyId) {
      http
        .get(`medical/pharmacies/${pharmacyId}/`)
        .then((res) => setReceipt((prevState) => ({ ...prevState, pharmacy: res.name })))
        .catch((err) => console.error(err));
    } else {
      setReceipt((prevState) => ({ ...prevState, pharmacy: '' }));
    }
  }, [pharmacyId]);

  const onSignatureUpdate = (signature: string) => {
    setReceipt((prevState) => ({
      ...prevState,
      signature: signature,
    }));
  };

  const onPharmacyUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReceipt((prevState) => ({
      ...prevState,
      pharmacy: e.target.value,
    }));
  };

  const isValid = useMemo(
    () => !!pharmacyId || receipt.pharmacy.length > 0,
    [receipt.pharmacy, pharmacyId],
  );

  const fileName = useMemo(
    () => `${format(new Date(), 'yyyy_MM_dd')}_${patient.last_name}.pdf`,
    [patient],
  );

  const formattedIssueDate = useMemo(() => format(new Date(), 'dd.MM.yyyy'), []);

  const pharmacySelected = useMemo(() => !!pharmacyId, [pharmacyId]);

  return {
    patient,
    doctor,
    ...receipt,
    onSignatureUpdate,
    onPharmacyUpdate,
    isValid,
    fileName,
    formattedIssueDate,
    pharmacySelected,
  };
}
